import { getChapterTitle, type Course } from "@repo/app-types/entities/course";
import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import type { TrainingChapterAssessment } from "@repo/app-types/entities/trainingChapterAssessment";
import { parseAverageGradingValue } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import {
  isNotNullNotUndefined,
  isNotUndefined,
} from "@repo/app-types/helpers/utilities";
import { renderPlural } from "@repo/ui/helpers/helpers";

export interface ChapterWithOptionalCriterionScore {
  id: string;
  index: number;
  topic: string | undefined;
  title: string;
  // null typically means it could not be calculated ("not enough signal"), undefined means missing
  value: number | null | undefined;
  commentary: string | undefined;
}

export interface ChapterWithCriterionScore extends ChapterWithOptionalCriterionScore {
  id: string;
  index: number;
  topic: string | undefined;
  title: string;
  value: number;
  commentary: string | undefined;
}

interface BaseCriterionInsights {
  firstChapter?: ChapterWithCriterionScore;
  lastChapter: ChapterWithCriterionScore;
  improvementRate?: number;
}

export interface CriterionInsightsWithoutImprovementRate extends BaseCriterionInsights {
  firstChapter?: undefined;
  lastChapter: ChapterWithCriterionScore;
  improvementRate?: undefined;
}

export interface CriterionInsightsWithImprovementRate extends BaseCriterionInsights {
  firstChapter: ChapterWithCriterionScore;
  lastChapter: ChapterWithCriterionScore;
  improvementRate: number;
}

export type CriterionInsights =
  | CriterionInsightsWithoutImprovementRate
  | CriterionInsightsWithImprovementRate;

export function computeCriterionInsights(
  course: Course,
  assessments: readonly TrainingChapterAssessment[],
  criterion: PlaybookCriterionNode,
): CriterionInsights | null {
  const scoredChapters = course.chapters.map(
    (chapter, chapterIndex): ChapterWithOptionalCriterionScore => {
      const assessment = assessments.find((a) => a.chapter_id === chapter.id);
      const grade =
        assessment?.grading.assessment_template?.criteria_grading[criterion.id];
      const value = parseAverageGradingValue(
        criterion.grading_value_type,
        grade?.value,
      );

      return {
        id: chapter.id,
        index: chapterIndex,
        topic: chapter.topic,
        title: getChapterTitle(chapterIndex, chapter),
        value: value?.value,
        commentary: grade?.commentary,
      };
    },
  );

  const chaptersWithScore = scoredChapters
    .map((chapter): ChapterWithCriterionScore | undefined =>
      isNotNullNotUndefined(chapter.value)
        ? { ...chapter, value: chapter.value }
        : undefined,
    )
    .filter(isNotUndefined);

  const firstChapterWithScore = chaptersWithScore[0];
  const lastChapterWithScore = chaptersWithScore[chaptersWithScore.length - 1];

  if (!firstChapterWithScore || !lastChapterWithScore) {
    return null;
  }

  if (chaptersWithScore.length === 1) {
    return {
      lastChapter: lastChapterWithScore,
    };
  }

  const startingValue = firstChapterWithScore.value;
  const endingValue = lastChapterWithScore.value;

  // Don't check for division by zero; we actually handle Infinite downstream
  const improvementRate = (endingValue - startingValue) / startingValue;

  return {
    firstChapter: firstChapterWithScore,
    lastChapter: lastChapterWithScore,
    improvementRate,
  };
}

export function renderAssessableParticipantCount(
  assessableParticipantsCount: number,
) {
  return renderPlural(assessableParticipantsCount, {
    zero: "no participants enrolled yet",
    one: "based on 1 enrolled participant",
    other: (count) =>
      `based on ${count.toFixed()} most recently enrolled participants`,
  });
}
